import { getBusinessSubTypeLabel, getRestaurantDetailById } from "@/utils";
import { useEffect, useState, useRef } from "react"; // Import useRef
import { Link, useParams } from "react-router-dom";
import Layout from "@/Components/layout/Layout";
import {
  FiPhone,
  FiClock,
  FiNavigation,
  FiMapPin,
  FiShare2,
  FiExternalLink,
  FiDownload,
  FiX,
  FiChevronLeft, // Import left arrow icon
  FiChevronRight, // Import right arrow icon
} from "react-icons/fi";

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination"; // Keep pagination styles if you use pagination

// Import required modules (Navigation is still needed for custom arrows)
import { Navigation, Pagination } from "swiper/modules";
import { HeroImagesSection } from "./hero-section";
import { DEFAULT_TYPE_ICONS } from "@/constants";
// You'll need a mapping for default icons/emojis if not already in BUSINESS_SUBTYPES

export default function BusinessDetail() {
  const { type, id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Refs for custom navigation arrows
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const prevButtonRef1 = useRef(null);
  const nextButtonRef1 = useRef(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getRestaurantDetailById(id)
      .then((data) => {
        setBusiness(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(`Failed to load ${type} details.`);
        setLoading(false);
      });
  }, [id]);

  const handleShare = () => {
    const shareData = {
      title: business?.name || "Business",
      text: `Check out ${business?.name} on Fly-Inn!`,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (loading)
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[70vh]">
          <div className="animate-pulse flex flex-col items-center">
            <div className="rounded-full bg-gray-200 h-16 w-16 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-48"></div>
          </div>
        </div>
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-8 text-center">
          <div className="bg-red-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold text-red-800 mb-4">
              Error Loading {type}
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </Layout>
    );

  if (!business)
    return (
      <Layout>
        <div className="max-w-4xl mx-auto p-8 text-center">
          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {type} Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The {type} you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );

  const businessImages = business?.images?.filter(
    (item) => item?.type === "photo"
  );
  const menuImages = business?.images?.filter((item) => item?.type === "menu");

  const iconClass =
    DEFAULT_TYPE_ICONS[business?.type] || DEFAULT_TYPE_ICONS.default;

  let business_discounts = [];

  try {
    if (business) {
      business_discounts = JSON.parse(business?.discounts);
    }
  } catch (error) {
    business_discounts = [];
  }

  return (
    <Layout>
      <main className="mx-auto w-full min-[834px]:max-w-[1220px] xl:max-w-[1380px]">
        {/* Modern Business Header */}

        <HeroSection businessImages={businessImages} />
        <div className="relative overflow-hidden md:rounded-2xl bg-white text-gray-800 shadow-md md:mb-10 !mt-12 border border-gray-100">
          <div className="relative z-20 flex flex-col md:flex-row gap-8 p-8">
            {/* Logo */}
            <div className="flex-shrink-0">
              {business?.business_logo ? (
                <img
                  src={`https://s3.amazonaws.com/flyinn-app-bucket/${business?.business_logo}`}
                  alt="Business Logo"
                  className="w-28 h-28 object-cover rounded-xl border-2 border-gray-200 shadow-md"
                />
              ) : (
                <div className="w-28 h-28 flex items-center justify-center rounded-xl bg-gray-100 text-gray-400 border-2 border-dashed border-gray-300 shadow-inner">
                  {/* Display the FontAwesome icon dynamically */}
                  <i className={`fa ${iconClass} text-5xl`}></i>
                  {/* Or if you prefer emojis, map emojis based on type too */}
                  {/* <span className="text-5xl">{getTypeEmoji(business.type)}</span> */}
                </div>
              )}
            </div>

            {/* Main Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold mb-2 text-gray-900">
                    {business?.name}
                  </h1>
                  {business?.tag_line && (
                    <div className="text-lg text-gray-600 mb-3 max-w-2xl">
                      {business.tag_line}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-700 mb-6">
                    <FiMapPin className="text-gray-500" />
                    <span className="text-gray-600">{business?.address}</span>
                  </div>
                </div>

                {/* Share Button */}
                <div className="flex-shrink-0">
                  <button
                    className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors group"
                    onClick={handleShare}
                  >
                    <FiShare2 className="text-gray-600 group-hover:text-red-600 transition-colors" />
                    <span className="font-medium text-gray-700">Share</span>
                    {copied && (
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded-md text-sm whitespace-nowrap shadow-lg">
                        Link copied!
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Info Row */}
              <div className="flex flex-wrap gap-3 mt-4">
                {/* Phone */}
                {business?.phone && (
                  <a
                    href={`tel:${business.phone}`}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                  >
                    <FiPhone className="text-gray-600" />
                    <span className="font-medium text-gray-700">
                      {business.phone}
                    </span>
                  </a>
                )}

                {/* Operational Hours */}
                {business?.operational_hrs && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <FiClock className="text-gray-600" />
                    <span className="text-gray-700">
                      {business.operational_hrs}
                    </span>
                  </div>
                )}

                {/* Distance from Runway */}
                {business?.distance_from_runway || (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <FiNavigation className="text-gray-600" />
                    <span className="text-gray-700">
                      {business.distance_from_runway} miles from{" "}
                      {business?.airport}
                    </span>
                  </div>
                )}

                {/* Nearest Airport */}
                {business?.nearest_airport && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <FiMapPin className="text-gray-600" />
                    <span className="text-gray-700">
                      {business.nearest_airport}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-2xl p-8 mb-10 shadow-md !mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
            About {business?.name}
          </h2>
          <p className="text-gray-700 leading-relaxed text-lg">
            {business?.business_details || "No description available."}
          </p>
        </div>

        {business_discounts?.length > 0 && (
          <div className="bg-white rounded-2xl p-8 mb-10 !mt-12 shadow-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100 flex items-center gap-2">
              <i className="fa fa-tags text-[--primary]"></i>
              Available Discounts
            </h2>

            <ul className="space-y-4 pl-0">
              {business_discounts?.map((discount, idx) => (
                <li
                  key={idx}
                  className="bg-gray-50 list-none  border border-gray-200 rounded-xl p-4 hover:shadow-sm transition"
                >
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-1">
                    <i className="fa fa-tag text-[--primary]" />
                    {discount.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {discount.description}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Menu Section with Carousel and Custom Arrows */}
        {menuImages && menuImages.length > 0 && (
          <div className="!mt-12 !mb-20 relative !px-2 md:!px-0 ">
            {" "}
            {/* Added relative and padding for arrow positioning */}
            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
              Our Menu
            </h2>
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={20}
              // navigation={true} // Remove this line
              pagination={{ clickable: true }}
              breakpoints={{
                0: {
                  slidesPerView: 1,
                  spaceBetween: 10,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 15,
                },
                1024: {
                  slidesPerView: 4,
                  spaceBetween: 20,
                },
              }}
              // Add onSwiper to initialize navigation after Swiper is ready
              onSwiper={(swiper) => {
                setTimeout(() => {
                  if (
                    swiper &&
                    swiper?.navigation &&
                    prevButtonRef.current &&
                    nextButtonRef.current
                  ) {
                    swiper.params.navigation.prevEl = prevButtonRef.current;
                    swiper.params.navigation.nextEl = nextButtonRef.current;
                    swiper.navigation.init();
                    swiper.navigation.update();
                  }
                }, 100); // You can increase this to 100ms if needed
              }}
              className="my-swipe"
            >
              {menuImages.map((img, idx) => (
                <SwiperSlide key={img.id || idx}>
                  <div
                    className="relative group overflow-hidden rounded-xl cursor-pointer"
                    onClick={() =>
                      setSelectedImage(
                        `https://s3.amazonaws.com/flyinn-app-bucket/${img.image}`
                      )
                    }
                  >
                    <img
                      src={`https://s3.amazonaws.com/flyinn-app-bucket/${img.image}`}
                      alt={`Menu ${idx + 1}`}
                      className="w-full h-64 max-h-64 min-h-64 object-cover rounded-xl transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                      <span className="text-white font-medium">
                        Menu Item {idx + 1}
                      </span>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Custom Navigation Arrows */}
            <button
              ref={prevButtonRef}
              className="absolute !top-[60%] -translate-y-1/2 !left-[30px] md:!left-0 z-10 p-2 md:p-3 bg-gray-900/50 hover:bg-red-700 text-white rounded-full transition-colors duration-300 shadow-lg transform -translate-x-1/2 md:translate-x-0" // Adjusted positioning for better mobile visibility
              aria-label="Previous menu item"
            >
              <FiChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
            </button>
            <button
              ref={nextButtonRef}
              className="absolute !top-[60%] -translate-y-1/2 !right-[30px] md:!right-0 z-10 p-2 md:p-3 bg-gray-900/50 hover:bg-red-700 text-white rounded-full transition-colors duration-300 shadow-lg transform translate-x-1/2 md:translate-x-0" // Adjusted positioning for better mobile visibility
              aria-label="Next menu item"
            >
              <FiChevronRight className="w-6 h-6 md:w-8 md:h-8" />
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex p-8 md:p-0 flex-col sm:flex-row gap-2 md:gap-6 !mt-12 !mb-20 justify-center">
          {/* Contact Button */}
          <a
            href={`tel:${business?.phone}`}
            className="relative group min-w-[300px] flex items-center justify-center gap-3 px-8 py-4 border-2 border-red-700 rounded-xl text-red-700 font-bold hover:text-white transition-all duration-300 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-800 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500 z-0"></div>
            <FiPhone className="text-lg z-10" />
            <span className="z-10 group-hover:text-white">
              Contact {business?.name}
            </span>
          </a>

          {/* Book Now Button */}
          <button
            onClick={() => {
              if (business?.url) {
                window.open(business.url, "_blank");
              } else {
                alert("Booking URL not available");
              }
            }}
            className="relative min-w-[300px] group flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-800 rounded-xl text-white font-bold hover:shadow-xl transition-all duration-300 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>
            <FiExternalLink className="text-lg z-10" />
            <span className="z-10 text-white">Book Now</span>
          </button>
        </div>

        {/* Menu Section with Carousel and Custom Arrows */}
        {business?.nearbyBusinesses &&
          business?.nearbyBusinesses?.length > 0 && (
            <div className="!mt-12 !mb-20 relative !px-2 md:!px-0 ">
              {" "}
              {/* Added relative and padding for arrow positioning */}
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100">
                Other {getBusinessSubTypeLabel(type)} nearby
              </h2>
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={20}
                // navigation={true} // Remove this line
                pagination={{ clickable: true }}
                breakpoints={{
                  0: {
                    slidesPerView: 1,
                    spaceBetween: 10,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 15,
                  },
                  1024: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                  },
                }}
                // Add onSwiper to initialize navigation after Swiper is ready
                onSwiper={(swiper) => {
                  setTimeout(() => {
                    if (
                      swiper &&
                      swiper?.navigation &&
                      prevButtonRef.current &&
                      nextButtonRef.current
                    ) {
                      swiper.params.navigation.prevEl = prevButtonRef1.current;
                      swiper.params.navigation.nextEl = nextButtonRef1.current;
                      swiper.navigation.init();
                      swiper.navigation.update();
                    }
                  }, 100); // You can increase this to 100ms if needed
                }}
                className="my-swipe-1"
              >
                {business?.nearbyBusinesses.map((business, idx) => {
                  const mainImage = business?.images?.find(
                    (item) => item?.type === "photo"
                  )?.image;

                  console.log({ mainImage, business });
                  return (
                    <SwiperSlide key={business.id || idx}>
                      <Link
                        to={`/${type}/${business.business_id}`}
                        className="block bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-all"
                      >
                        <div className="relative h-48">
                          {mainImage ? (
                            <img
                              src={`https://s3.amazonaws.com/flyinn-app-bucket/${mainImage}`}
                              alt={business.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="bg-gray-100 w-full h-full flex items-center justify-center">
                              <span className="text-5xl">🍴</span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-bold text-lg text-gray-900 truncate">
                            {business.name}
                          </h3>
                          <p className="text-gray-600 text-sm mt-1 truncate">
                            {business.address}
                          </p>
                          <div className="mt-3 flex items-center">
                            <FiNavigation
                              className="text-gray-500 mr-1"
                              size={14}
                            />
                            <span className="text-gray-700 text-sm">
                              {business.distance_from_runway} miles from{" "}
                              {business?.airport}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
              {/* Custom Navigation Arrows */}
              {business?.nearbyBusinesses?.length > 4 && (
                <>
                  <button
                    ref={prevButtonRef1}
                    className="absolute !top-[60%] -translate-y-1/2 !left-[30px] md:!left-0 z-10 p-2 md:p-3 bg-gray-900/50 hover:bg-red-700 text-white rounded-full transition-colors duration-300 shadow-lg transform -translate-x-1/2 md:translate-x-0" // Adjusted positioning for better mobile visibility
                    aria-label="Previous menu item"
                  >
                    <FiChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                  </button>
                  <button
                    ref={nextButtonRef1}
                    className="absolute !top-[60%] -translate-y-1/2 !right-[30px] md:!right-0 z-10 p-2 md:p-3 bg-gray-900/50 hover:bg-red-700 text-white rounded-full transition-colors duration-300 shadow-lg transform translate-x-1/2 md:translate-x-0" // Adjusted positioning for better mobile visibility
                    aria-label="Next menu item"
                  >
                    <FiChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                  </button>
                </>
              )}
            </div>
          )}
      </main>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[99999] px-4">
          <div className="relative bg-white rounded-xl overflow-hidden max-w-3xl w-full">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white bg-black/40 hover:bg-black/60 rounded-full p-2 z-10"
            >
              <FiX size={20} />
            </button>

            {/* Image */}
            <img
              src={selectedImage}
              alt="Menu Full"
              className="w-full h-[70vh] object-contain bg-black"
            />

            {/* Download Button */}
            <div className="p-4 bg-white flex justify-end">
              <a
                href={selectedImage}
                download
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
              >
                <FiDownload />
                Download Image
              </a>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const HeroSection = ({ businessImages = [] }) => {
  if (!businessImages.length) return null;

  return (
    <>
      <HeroImagesSection images={businessImages} />
    </>
  );
};

// Add this to your CSS for pattern effect (ensure these are in your global CSS file)
/*
  .pattern-dots {
    background-image: radial-gradient(currentColor 1px, transparent 1px);
    background-size: calc(10 * 1px) calc(10 * 1px);
  }

  // Custom Swiper pagination styles
  .swiper-pagination-bullet {
    background-color: #d1d5db; // Light gray for inactive bullets
    opacity: 1;
  }

  .swiper-pagination-bullet-active {
    background-color: #dc2626; // Red for active bullet
  }
*/
